import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FolderInput, Loader2, X, Podcast } from "lucide-react";
import { api } from "@/lib/api";
import { fmtTime } from "./snippetUtil";

const CHUNK = 4 * 1024 * 1024;
const ACCEPT = ".mp3,.wav,.m4a,.mp4,.mov,.webm";

export function UploadImportPanel({ onDone }) {
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [rightsAck, setRightsAck] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [sources, setSources] = useState(null);
  const [importingId, setImportingId] = useState("");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (importOpen) setImportOpen(false);
      else if (pending && !uploading) setPending(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [importOpen, pending, uploading]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 500 * 1024 * 1024) return toast.error("File exceeds the 500 MB limit");
    setPending(f);
    setRightsAck(false);
    setProgress(0);
  };

  const startUpload = async () => {
    if (!pending || !rightsAck) return;
    setUploading(true);
    try {
      const init = await api.post("/snippets/upload/init", {
        filename: pending.name, size: pending.size, rights_ack: true,
      });
      const upId = init.data.upload_id;
      for (let off = 0; off < pending.size; off += CHUNK) {
        const slice = pending.slice(off, off + CHUNK);
        await api.post(`/snippets/upload/chunk/${upId}`, slice, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        setProgress(Math.min(99, Math.round(((off + CHUNK) / pending.size) * 100)));
      }
      const done = await api.post(`/snippets/upload/complete/${upId}`);
      setProgress(100);
      toast.success("Upload complete");
      setPending(null);
      onDone(done.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openImport = async () => {
    setImportOpen(true);
    try {
      const r = await api.get("/snippets/import/sources");
      setSources(r.data.sources || []);
    } catch {
      setSources([]);
    }
  };

  const doImport = async (sourceId, itemId) => {
    setImportingId(itemId);
    try {
      const r = await api.post("/snippets/import", { source: sourceId, id: itemId });
      toast.success("Episode imported");
      setImportOpen(false);
      onDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImportingId("");
    }
  };

  return (
    <div className="flex gap-2">
      <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={pick} data-testid="snip-upload-input" />
      <button
        onClick={() => fileRef.current?.click()}
        className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-bg-base hover:opacity-90"
        data-testid="snip-upload-btn">
        <Upload size={14} /> Upload media
      </button>
      <button
        onClick={openImport}
        className="type-button-label flex items-center gap-1.5 rounded-md border border-line px-3 py-2 text-ink-primary hover:bg-white/5"
        data-testid="snip-import-btn">
        <FolderInput size={14} /> Import
      </button>

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="snip-upload-modal"
          onClick={() => !uploading && setPending(null)}>
          <div className="w-full max-w-md rounded-xl border border-line bg-bg-surface p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="type-section-heading text-ink-primary">Upload recording</h3>
              {!uploading && (
                <button onClick={() => setPending(null)} className="text-ink-muted hover:text-ink-primary" data-testid="snip-upload-cancel">
                  <X size={16} />
                </button>
              )}
            </div>
            <div className="type-list-title text-ink-primary truncate">{pending.name}</div>
            <div className="type-list-subtitle text-ink-muted mb-4">
              {pending.size >= 1048576 ? `${(pending.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(pending.size / 1024))} KB`}
            </div>
            <label className="flex items-start gap-2 mb-4 cursor-pointer">
              <input type="checkbox" checked={rightsAck} onChange={(e) => setRightsAck(e.target.checked)}
                className="mt-0.5 accent-amber-500" data-testid="snip-rights-ack" />
              <span className="type-body text-ink-muted">
                I confirm my station holds the rights to redistribute this recording on social media.
              </span>
            </label>
            {uploading ? (
              <div>
                <div className="h-2 w-full rounded bg-white/10 overflow-hidden">
                  <div className="h-2 bg-brand-amber transition-all" style={{ width: `${progress}%` }} />
                </div>
                <div className="type-timestamp text-ink-muted mt-2">Uploading… {progress}%</div>
              </div>
            ) : (
              <button onClick={startUpload} disabled={!rightsAck}
                className="type-button-label w-full rounded-md bg-brand-amber px-4 py-2 text-bg-base disabled:opacity-40"
                data-testid="snip-upload-start">
                Start upload
              </button>
            )}
          </div>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="snip-import-modal"
          onClick={() => setImportOpen(false)}>
          <div className="w-full max-w-lg rounded-xl border border-line bg-bg-surface p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="type-section-heading text-ink-primary">Import from your studios</h3>
              <button onClick={() => setImportOpen(false)} className="text-ink-muted hover:text-ink-primary" data-testid="snip-import-close">
                <X size={16} />
              </button>
            </div>
            {sources === null ? (
              <div className="flex items-center gap-2 type-body text-ink-muted py-6"><Loader2 size={14} className="animate-spin" /> Loading sources…</div>
            ) : sources.every((s) => !s.items?.length) ? (
              <div className="type-body text-ink-muted py-6" data-testid="snip-import-empty">
                No importable recordings yet — render a Podcast Studio episode first, or upload a file directly.
              </div>
            ) : (
              sources.map((src) => (
                <div key={src.id} className="mb-2">
                  <div className="type-status-badge font-mono uppercase tracking-wider text-ink-muted mb-2">// {src.label}</div>
                  <div className="max-h-72 overflow-y-auto space-y-1">
                    {src.items.map((it) => (
                      <div key={it.id} className="flex items-center gap-3 rounded-md border border-line/40 px-3 py-2">
                        <Podcast size={14} className="text-ink-muted shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="type-list-title text-ink-primary truncate">{it.title}</div>
                          <div className="type-list-subtitle text-ink-muted">
                            {it.duration ? fmtTime(it.duration) : (it.created_at || "").slice(0, 10) || "—"}
                          </div>
                        </div>
                        <button onClick={() => doImport(src.id, it.id)} disabled={!!importingId}
                          className="type-button-label rounded-md border border-line px-2.5 py-1 text-ink-primary hover:bg-white/5 disabled:opacity-40"
                          data-testid={`snip-import-item-${it.id}`}>
                          {importingId === it.id ? <Loader2 size={13} className="animate-spin" /> : "Import"}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
